// src/modules/workspace/DistributionLab.jsx
// ☆ PIN TO DASHBOARD available on every chart card header
import React, { useState, useMemo, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ReferenceLine,
} from 'recharts';
import { C, PALETTE, colStats, getHistogram, getValueCounts, inferTypes, PinButton, LabLoader, ChartTip } from './wsShared';
import { EChart } from './DashboardEngine';

// ── SVG box-plot ──────────────────────────────────────────────────────────────
function BoxPlot({ stats, color=C.cyan, width=300, height=72 }) {
  if(!stats) return null;
  const { min, q1, median, q3, max, mean } = stats;
  const range = max-min||1;
  const px = v => ((v-min)/range)*(width-48)+24;
  return (
    <svg width={width} height={height} style={{overflow:'visible',maxWidth:'100%'}}>
      <line x1={px(min)} x2={px(max)} y1={height/2} y2={height/2} stroke={color+'50'} strokeWidth={2}/>
      {[min,max].map((v,i)=><line key={i} x1={px(v)} x2={px(v)} y1={height/2-9} y2={height/2+9} stroke={color+'80'} strokeWidth={2}/>)}
      <rect x={px(q1)} y={height/2-14} width={Math.max(px(q3)-px(q1),2)} height={28} fill={color+'22'} stroke={color} strokeWidth={1.5} rx={2}/>
      <line x1={px(median)} x2={px(median)} y1={height/2-14} y2={height/2+14} stroke={color} strokeWidth={2.5}/>
      <circle cx={px(mean)} cy={height/2} r={4} fill={C.gold}/>
      {[{v:min,a:'start'},{v:median,a:'middle'},{v:max,a:'end'}].map(({v,a},i)=>(
        <text key={i} x={px(v)} y={height-2} fontSize={8} fill={C.t4} textAnchor={a}>{v.toFixed(2)}</text>
      ))}
      <text x={px(mean)} y={8} fontSize={8} fill={C.gold} textAnchor="middle">μ={mean.toFixed(2)}</text>
    </svg>
  );
}

// ── Card header with ☆ pin ───────────────────────────────────────────────────
function CardHeader({ col, type, color, chartDesc, onRemove }) {
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 12px',background:C.deep,borderBottom:`1px solid ${C.b0}` }}>
      <div style={{ display:'flex',alignItems:'center',gap:7 }}>
        <span style={{ width:8,height:8,borderRadius:'50%',background:color,display:'inline-block',flexShrink:0 }}/>
        <span style={{ fontFamily:'JetBrains Mono,monospace',fontSize:11,color:C.t1,fontWeight:700 }}>{col}</span>
        <span style={{ fontSize:8,padding:'1px 5px',borderRadius:2,background:color+'20',color,border:`1px solid ${color}44` }}>{type}</span>
      </div>
      <div style={{ display:'flex',gap:5,alignItems:'center' }}>
        <PinButton chart={chartDesc} small />
        {onRemove && <button onClick={onRemove} title="Hide" style={{ background:'none',border:'none',color:C.t3,cursor:'pointer',fontSize:14,padding:'1px 4px',lineHeight:1 }}>×</button>}
      </div>
    </div>
  );
}

// ── Single column card ────────────────────────────────────────────────────────
// Receives pre-computed data from parent — no heavy work inside render
function ColCard({ col, type, colorIdx, onRemove, stats, hist, counts, catMeta }) {
  const color   = PALETTE[colorIdx % PALETTE.length];
  const skewLbl = stats ? (Math.abs(stats.skew)<0.2?'symmetric':stats.skew>0?'right-skewed':'left-skewed') : '';

  const chartDesc = type==='numeric'
    ? { id:`dist_hist_${col}`, type:'hist', col, title:`${col} — Distribution`, subtitle:'histogram · distribution lab', cols:[col], reportSection:'distributions' }
    : { id:`dist_cat_${col}`,  type:'hbar', col, title:`${col} — Value Counts`,  subtitle:'categorical · distribution lab', reportSection:'distributions' };

  return (
    <div style={{ background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden' }}>
      <CardHeader col={col} type={type} color={color} chartDesc={chartDesc} onRemove={onRemove} />

      {/* Stats strip (numeric) */}
      {stats&&(
        <div style={{ display:'flex',overflowX:'auto',borderBottom:`1px solid ${C.b0}` }}>
          {[
            {l:'MEAN',v:stats.mean,c:color},{l:'STD',v:stats.std,c:C.t3},
            {l:'MIN',v:stats.min,c:C.t4},{l:'MED',v:stats.median,c:color},
            {l:'MAX',v:stats.max,c:C.t4},{l:'SKEW',v:stats.skew,c:Math.abs(stats.skew)>0.5?C.gold:C.green},
            {l:'OUTL',v:stats.outlierCount,c:stats.outlierCount>0?C.red:C.green},
            {l:'NULL',v:stats.nulls,c:stats.nulls>0?C.red:C.green},
          ].map(m=>(
            <div key={m.l} style={{ padding:'5px 8px',borderRight:`1px solid ${C.b0}`,flexShrink:0 }}>
              <div style={{ fontSize:7,color:C.t4,letterSpacing:'.1em' }}>{m.l}</div>
              <div style={{ fontFamily:'JetBrains Mono,monospace',fontSize:10,color:m.c,fontWeight:700 }}>{m.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Stats strip (categorical) */}
      {counts&&(
        <div style={{ display:'flex',overflowX:'auto',borderBottom:`1px solid ${C.b0}` }}>
      
      {[{l:'UNIQUE',v:catMeta?.unique??0},
            {l:'TOP',v:(counts[0]?.name??'—').slice(0,12)},
            {l:'TOP%',v:`${counts[0]?.pct??0}%`},
            {l:'NULL',v:catMeta?.nulls??0},
          ].map(m=>(
            <div key={m.l} style={{ padding:'5px 8px',borderRight:`1px solid ${C.b0}`,flexShrink:0 }}>
              <div style={{ fontSize:7,color:C.t4,letterSpacing:'.1em' }}>{m.l}</div>
              <div style={{ fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.t2,fontWeight:700 }}>{String(m.v)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Histogram */}
      {hist&&(
        <div style={{ padding:'8px 10px 0' }}>
          <div style={{ fontSize:8,color:C.t4,marginBottom:3,fontFamily:'JetBrains Mono,monospace' }}>HISTOGRAM · {skewLbl}</div>
          <EChart height={100} option={{
            backgroundColor:'transparent',
            tooltip:{trigger:'axis',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
            grid:{top:4,right:4,bottom:20,left:4,containLabel:true},
            xAxis:{type:'value',min:'dataMin',max:'dataMax',axisLabel:{color:C.t4,fontSize:7},axisLine:{lineStyle:{color:C.b1}},axisTick:{show:false},splitLine:{show:false}},
            yAxis:{type:'value',axisLabel:{color:C.t4,fontSize:7},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
            series:[{
              type:'bar',barWidth:'95%',
              data:hist.map(d=>[d.label,d.count]),
              itemStyle:{color,opacity:0.85,borderRadius:[2,2,0,0]},
              ...(stats?{markLine:{silent:true,symbol:'none',lineStyle:{color:C.gold,type:'dashed',width:1.5},data:[{xAxis:stats.mean,label:{show:false}}]}}:{})
            }]
          }}/>
        </div>
      )}

      {/* Value-counts bar */}
      {counts&&(
        <div style={{ padding:'8px 10px 0' }}>
          <div style={{ fontSize:8,color:C.t4,marginBottom:3,fontFamily:'JetBrains Mono,monospace' }}>VALUE COUNTS (TOP {counts.length})</div>
          <EChart height={Math.min(counts.length*22+20,160)} option={{
            backgroundColor:'transparent',
            tooltip:{trigger:'axis',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
            grid:{top:0,right:24,bottom:0,left:4,containLabel:true},
            xAxis:{type:'value',axisLabel:{color:C.t4,fontSize:7},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
            yAxis:{type:'category',data:counts.map(d=>String(d.name)),axisLabel:{color:C.t2,fontSize:8},axisLine:{show:false},axisTick:{show:false}},
            series:[{type:'bar',data:counts.map((d,i)=>({value:d.count,itemStyle:{color:PALETTE[i%PALETTE.length]}})),barMaxWidth:16,itemStyle:{borderRadius:[0,2,2,0]}}]
          }}/>
        </div>
      )}

      {/* Box plot */}
      {stats&&(
        <div style={{ padding:'6px 10px 10px' }}>
          <div style={{ fontSize:8,color:C.t4,marginBottom:3,fontFamily:'JetBrains Mono,monospace' }}>
            BOX PLOT · IQR={stats.iqr} · {stats.outlierCount} outlier{stats.outlierCount!==1?'s':''}
          </div>
          <BoxPlot stats={stats} color={color} width={280}/>
        </div>
      )}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function DistributionLab({ ds, precomputedColData = null }) {
  const [view,    setView]    = useState('all');
  const [selCol,  setSelCol]  = useState('');
  const [bins,    setBins]    = useState(20);
  const [hidden,  setHidden]  = useState(new Set());
  const [colData, setColData] = useState(null); // null = computing

  const { rows, headers } = ds;

  // Async batch: compute types + stats + hist + counts for ALL columns
  // Runs after the browser paints so the LabLoader is visible immediately
  useEffect(() => {
    // If precomputed data provided, use it directly (View Report mode — full dataset)
    if (precomputedColData) {
      setColData(precomputedColData);
      return;
    }
    setColData(null);
    const t = setTimeout(() => {
      const typesArr = inferTypes(headers, rows);
      const typeMap  = {};
      typesArr.forEach(ti => { typeMap[ti.col] = ti.type; });
      const perCol = {};
      headers.forEach(col => {
        const type   = typeMap[col] || 'categorical';
        const stats  = type === 'numeric' ? colStats(rows, col) : null;
        const counts = type === 'categorical' ? getValueCounts(rows, col, 15) : null;
        const catMeta = type === 'categorical' ? {
          unique: new Set(rows.map(r => r[col]).filter(Boolean)).size,
          nulls:  rows.filter(r => !r[col]).length,
        } : null;
        perCol[col] = { type, stats, hist: null, counts, catMeta };
      });
      // Histograms in a second pass (bins may have changed)
      headers.forEach(col => {
        if (perCol[col].type === 'numeric') {
          perCol[col].hist = getHistogram(rows, col, bins);
        }
      });
      setColData({ typeMap, perCol });
    }, 0);
    return () => clearTimeout(t);
  }, [rows, headers, bins, precomputedColData]); // eslint-disable-line

  const typeMap  = colData?.typeMap || {};
  // visible / hideable are cheap filters on pre-computed colData
  const visible  = colData
    ? headers.filter(h => {
        if (hidden.has(h)) return false;
        const d = colData.perCol[h];
        if (!d) return false;
        if (d.type === 'categorical' && d.catMeta && d.catMeta.unique / rows.length > 0.9 && rows.length > 30) return false;
        return true;
      })
    : [];
  const hideable = headers.filter(h => hidden.has(h));
  const hide = h => setHidden(s => new Set([...s, h]));
  const show = h => setHidden(s => { const n = new Set(s); n.delete(h); return n; });

  // SINGLE view — user picks one column interactively; 1-column compute is fast
  const sStats  = useMemo(() => selCol && typeMap[selCol]==='numeric' ? colStats(rows,selCol) : null, [rows,selCol,typeMap]);
  const sHist   = useMemo(() => sStats ? getHistogram(rows,selCol,bins) : null, [rows,selCol,bins,sStats]);
  const sCounts = useMemo(() => selCol && typeMap[selCol]==='categorical' ? getValueCounts(rows,selCol,30) : null, [rows,selCol,typeMap]);

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:14 }}>

      {/* Controls */}
      <div style={{ display:'flex',gap:10,alignItems:'center',flexWrap:'wrap' }}>
        <div style={{ display:'flex',gap:2,background:C.deep,border:`1px solid ${C.b0}`,borderRadius:3,padding:2 }}>
          {['all','single'].map(v=>(
            <button key={v} onClick={()=>setView(v)} style={{
              padding:'5px 14px',border:'none',borderRadius:2,cursor:'pointer',
              background:view===v?C.cyan:'transparent',color:view===v?C.void:C.t3,
              fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,textTransform:'uppercase',
            }}>{v==='all'?'ALL COLUMNS':'SINGLE COLUMN'}</button>
          ))}
        </div>
        {view==='single'&&(
          <select value={selCol} onChange={e=>setSelCol(e.target.value)} style={{
            background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'6px 10px',fontSize:11,
            fontFamily:'JetBrains Mono,monospace',borderRadius:3,cursor:'pointer',outline:'none',
          }}>
            <option value="">— select column —</option>
            {headers.map(h=><option key={h} value={h}>{h} ({typeMap[h]})</option>)}
          </select>
        )}
        <div style={{ display:'flex',alignItems:'center',gap:6 }}>
          <span style={{ fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace' }}>BINS</span>
          <input type="range" min={5} max={60} value={bins} onChange={e=>setBins(+e.target.value)} style={{accentColor:C.cyan,width:90}}/>
          <span style={{ fontSize:10,color:C.cyan,fontFamily:'JetBrains Mono,monospace',minWidth:18 }}>{bins}</span>
        </div>
        <span style={{ marginLeft:'auto',fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>
          {visible.length}/{headers.length} cols · ☆ to pin any chart
        </span>
      </div>

      {/* Hidden pill row */}
      {view==='all'&&hideable.length>0&&(
        <div style={{ display:'flex',gap:5,flexWrap:'wrap',alignItems:'center' }}>
          <span style={{ fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>HIDDEN:</span>
          {hideable.map(c=>(
            <button key={c} onClick={()=>show(c)} style={{
              padding:'2px 8px',border:`1px solid ${C.b1}`,background:'transparent',color:C.t3,
              fontFamily:'JetBrains Mono,monospace',fontSize:9,borderRadius:2,cursor:'pointer',
            }}>+ {c}</button>
          ))}
        </div>
      )}

      {/* ALL VIEW */}
      {view==='all'&&(
        !colData
          ? <LabLoader rows={rows.length} label="COMPUTING DISTRIBUTIONS" />
          : visible.length===0
            ? <div style={{ padding:40,textAlign:'center',color:C.t4,fontSize:11 }}>All columns hidden</div>
            : <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:14 }}>
                {visible.map((col,i)=>{
                  const d = colData.perCol[col];
                  return (
                    <ColCard key={col} col={col} type={d.type} colorIdx={i}
                      stats={d.stats} hist={d.hist} counts={d.counts} catMeta={d.catMeta}
                      onRemove={()=>hide(col)} bins={bins}/>
                  );
                })}
              </div>
      )}

      {/* SINGLE VIEW */}
      {view==='single'&&!selCol&&(
        <div style={{ padding:40,textAlign:'center',color:C.t4 }}>
          <div style={{ fontSize:24,marginBottom:8,opacity:0.3 }}>⬟</div>
          <div style={{ fontSize:12,color:C.t3 }}>Select a column above</div>
        </div>
      )}

      {view==='single'&&selCol&&(
        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>

          {/* Stats grid */}
          {sStats&&(
            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8 }}>
              {[
                {l:'COUNT',v:sStats.count,c:C.cyan},{l:'MEAN',v:sStats.mean,c:C.cyan},
                {l:'STD',v:sStats.std,c:C.violet},{l:'SKEWNESS',v:sStats.skew,c:Math.abs(sStats.skew)>0.5?C.gold:C.green},
                {l:'MIN',v:sStats.min,c:C.t2},{l:'Q25',v:sStats.q1,c:C.t2},
                {l:'MEDIAN',v:sStats.median,c:C.t1},{l:'Q75',v:sStats.q3,c:C.t2},
                {l:'MAX',v:sStats.max,c:C.t2},{l:'IQR',v:sStats.iqr,c:C.violet},
                {l:'OUTLIERS',v:sStats.outlierCount,c:sStats.outlierCount>0?C.red:C.green},
                {l:'NULLS',v:sStats.nulls,c:sStats.nulls>0?C.red:C.green},
              ].map(m=>(
                <div key={m.l} style={{ background:C.raised,border:`1px solid ${C.b1}`,borderLeft:`2px solid ${m.c}`,padding:'9px 11px',borderRadius:3 }}>
                  <div style={{ fontSize:8,color:C.t4,letterSpacing:'.12em' }}>{m.l}</div>
                  <div style={{ fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:17,color:m.c }}>{m.v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Large histogram */}
          {sHist&&(
            <div style={{ background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,padding:16 }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                <div style={{ fontSize:10,color:C.cyan,letterSpacing:'.13em',fontFamily:'JetBrains Mono,monospace' }}>
                  HISTOGRAM — {selCol} · {bins} bins
                </div>
                <PinButton chart={{ id:`dist_hist_single_${selCol}`, type:'hist', col:selCol, title:`${selCol} — Distribution`, subtitle:'histogram · single view', reportSection:'distributions' }} small />
              </div>
              <div style={{ height:260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sHist} barCategoryGap="2%">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.b0} vertical={false}/>
                    <XAxis dataKey="label" tick={{fill:C.t4,fontSize:9}} interval="preserveStartEnd"/>
                    <YAxis tick={{fill:C.t4,fontSize:9}}/>
                    <Tooltip content={<ChartTip/>}/>
                    {sStats&&<ReferenceLine x={String(sStats.mean.toFixed(3))} stroke={C.gold} strokeDasharray="4 4" label={{value:'μ',fill:C.gold,fontSize:10}}/>}
                    <Bar dataKey="count" fill={C.cyan} fillOpacity={0.85} radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Large box plot */}
          {sStats&&(
            <div style={{ background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,padding:16 }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                <div style={{ fontSize:10,color:C.violet,letterSpacing:'.13em',fontFamily:'JetBrains Mono,monospace' }}>BOX PLOT — {selCol}</div>
                <PinButton chart={{ id:`dist_box_${selCol}`, type:'boxplot', col:selCol, title:`${selCol} — Box Plot`, subtitle:'box plot · distribution', reportSection:'distributions' }} small />
              </div>
              <BoxPlot stats={sStats} color={C.violet} width={560} height={90}/>
              <div style={{ marginTop:10,fontSize:11,color:C.t3,lineHeight:1.7 }}>
                Distribution is <strong style={{color:C.t1}}>{Math.abs(sStats.skew)<0.2?'roughly symmetric':sStats.skew>0?'right-skewed (positive skew)':'left-skewed (negative skew)'}</strong>.
                {sStats.outlierCount>0&&<> <strong style={{color:C.red}}>{sStats.outlierCount} outliers</strong> detected outside 1.5×IQR fences.</>}
              </div>
            </div>
          )}

          {/* Categorical value counts */}
          {sCounts&&(
            <div style={{ background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,padding:16 }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                <div style={{ fontSize:10,color:C.gold,letterSpacing:'.13em',fontFamily:'JetBrains Mono,monospace' }}>VALUE COUNTS — {selCol}</div>
                <PinButton chart={{ id:`dist_cat_single_${selCol}`, type:'hbar', col:selCol, title:`${selCol} — Value Counts`, subtitle:'categorical · single view', reportSection:'distributions' }} small />
              </div>
              <div style={{ height:Math.min(sCounts.length*26+30,420) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sCounts} layout="vertical" barCategoryGap="6%">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.b0} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.t4,fontSize:9}}/>
                    <YAxis dataKey="name" type="category" tick={{fill:C.t2,fontSize:10}} width={130}/>
                    <Tooltip content={<ChartTip/>}/>
                    <Bar dataKey="count" radius={[0,3,3,0]}>
                      {sCounts.map((_,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
